import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useApi } from '../api/useApi';
import { useSession } from '../auth/SessionProvider';
import { featureNav } from '../config/featureNav';
import type { ActivePresenceDto } from '../api/types';

const REFRESH_MS = 15_000;
const WINDOW_MIN = 5;

function ago(seconds: number): string {
  if (seconds < 10) return 'gerade eben';
  if (seconds < 60) return `vor ${seconds} Sek`;
  if (seconds < 3600) return `vor ${Math.floor(seconds / 60)} Min`;
  return `vor ${Math.floor(seconds / 3600)} Std`;
}

function Dot({ seconds }: { seconds: number }) {
  const color = seconds < 90 ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteYellowForeground1;
  return (
    <span
      title={seconds < 90 ? 'online' : 'inaktiv'}
      style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: color }}
    />
  );
}

export function ActiveUsersPage() {
  const api = useApi();
  const { me } = useSession();
  const [rows, setRows] = useState<ActivePresenceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .getActiveUsers(WINDOW_MIN)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const onlineCount = useMemo(() => rows.filter((r) => r.secondsAgo < 90).length, [rows]);

  // Route → lesbarer Seitenname. Bewusst zur Render-Zeit (nicht auf Modul-Ebene),
  // da featureNav diese Seite importiert (zirkulärer Import → sonst undefined beim Load).
  const pageLabels = useMemo(() => {
    const map: Record<string, string> = { '/settings': 'Einstellungen' };
    for (const section of featureNav) {
      for (const item of section.items) {
        map[item.key] = `${section.label} – ${item.text}`;
      }
    }
    return map;
  }, []);
  const pageLabel = (path?: string | null) => (path ? pageLabels[path] ?? path : '—');

  if (me && !me.isAdmin) {
    return (
      <div>
        <PageHeader title="Aktive Nutzer" />
        <MessageBar intent="warning">
          <MessageBarBody>Diese Übersicht ist M365 Adminbox-Admins vorbehalten.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Aktive Nutzer"
        description="Wer ist gerade in der App aktiv — gemessen an tatsächlicher Aktivität (Seitenaufrufe, API-Aufrufe, Heartbeat), nicht an ausgestellten Token."
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM }}>
        <Badge appearance="filled" color="success">{onlineCount} online</Badge>
        <Badge appearance="tint">{rows.length} aktiv (letzte {WINDOW_MIN} Min)</Badge>
        {loading ? <Spinner size="tiny" /> : null}
        <Button size="small" appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={load}>
          Aktualisieren
        </Button>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>aktualisiert automatisch alle 15 s</Caption1>
      </div>

      {error ? (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {rows.length === 0 && !loading ? (
        <Text>Zurzeit ist niemand aktiv.</Text>
      ) : (
        <Table size="small" aria-label="Aktive Nutzer">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: 36 }} />
              <TableHeaderCell>Nutzer</TableHeaderCell>
              <TableHeaderCell>Rolle</TableHeaderCell>
              <TableHeaderCell>Aktuelle Seite</TableHeaderCell>
              <TableHeaderCell>Zuletzt aktiv</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.userObjectId}>
                <TableCell><Dot seconds={r.secondsAgo} /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text weight="semibold" size={200} className="pii">{r.displayName}</Text>
                    <Caption1 className="pii" style={{ color: tokens.colorNeutralForeground3 }}>{r.upn}</Caption1>
                  </div>
                </TableCell>
                <TableCell>
                  {r.roles.length > 0 ? (
                    r.roles.map((role) => (
                      <Badge key={role} appearance="tint" color={role === 'Admin' ? 'brand' : 'informative'} size="small" style={{ marginInlineEnd: 4 }}>
                        {role}
                      </Badge>
                    ))
                  ) : (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>
                  )}
                </TableCell>
                <TableCell>{pageLabel(r.lastPath)}</TableCell>
                <TableCell>{ago(r.secondsAgo)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
